import { useState, useEffect } from 'react'
import { 
  Lightbulb, 
  MessageSquare, 
  User, 
  Calendar, 
  ChevronDown, 
  ChevronUp,
  ExternalLink,
  CheckCircle,
  Clock,
  XCircle,
  AlertCircle,
  Filter
} from 'lucide-react'

const API_BASE = import.meta.env.VITE_API_URL || ''

export default function FeatureSuggestions({ adminEmail }) {
  const [suggestions, setSuggestions] = useState([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState('all') // all, new, reviewing, planned, completed
  const [expandedId, setExpandedId] = useState(null)
  const [chatContext, setChatContext] = useState(null)
  const [loadingChat, setLoadingChat] = useState(false)

  useEffect(() => {
    fetchSuggestions()
  }, [filter])

  const fetchSuggestions = async () => {
    setLoading(true)
    try {
      const url = filter === 'all' 
        ? `${API_BASE}/admin/feature-suggestions`
        : `${API_BASE}/admin/feature-suggestions?status=${filter}`
      
      const res = await fetch(url, {
        headers: { 'x-admin-email': adminEmail }
      })
      const data = await res.json()
      setSuggestions(data.suggestions || [])
    } catch (err) {
      console.error('Failed to fetch suggestions:', err)
    } finally {
      setLoading(false)
    }
  }

  const updateStatus = async (id, newStatus) => {
    try {
      await fetch(`${API_BASE}/admin/feature-suggestions/${id}`, {
        method: 'PATCH',
        headers: { 
          'Content-Type': 'application/json',
          'x-admin-email': adminEmail 
        },
        body: JSON.stringify({ status: newStatus })
      })
      fetchSuggestions()
    } catch (err) {
      console.error('Failed to update status:', err)
    }
  }

  const loadChatContext = async (suggestion) => {
    if (expandedId === suggestion.id) {
      setExpandedId(null)
      setChatContext(null)
      return
    }
    
    setExpandedId(suggestion.id)
    setLoadingChat(true)
    
    try {
      // Fetch full chat history for this project
      const res = await fetch(`${API_BASE}/admin/chat-context/${suggestion.project_id}?highlight_index=${suggestion.highlight_start_index}`, {
        headers: { 'x-admin-email': adminEmail }
      })
      const data = await res.json()
      setChatContext(data)
    } catch (err) {
      console.error('Failed to load chat context:', err)
      // Fallback to stored context
      setChatContext({
        before: suggestion.context_before || [],
        highlighted: suggestion.user_original_message,
        after: suggestion.context_after || []
      })
    } finally {
      setLoadingChat(false)
    }
  }

  const getStatusBadge = (status) => {
    switch (status) {
      case 'new':
        return <span className="px-2 py-1 bg-blue-500/20 text-blue-400 rounded-full text-xs flex items-center gap-1"><AlertCircle className="w-3 h-3" /> New</span>
      case 'reviewing':
        return <span className="px-2 py-1 bg-yellow-500/20 text-yellow-400 rounded-full text-xs flex items-center gap-1"><Clock className="w-3 h-3" /> Reviewing</span>
      case 'planned':
        return <span className="px-2 py-1 bg-purple-500/20 text-purple-400 rounded-full text-xs flex items-center gap-1"><Lightbulb className="w-3 h-3" /> Planned</span>
      case 'in_progress':
        return <span className="px-2 py-1 bg-orange-500/20 text-orange-400 rounded-full text-xs flex items-center gap-1"><Clock className="w-3 h-3" /> In Progress</span>
      case 'completed':
        return <span className="px-2 py-1 bg-green-500/20 text-green-400 rounded-full text-xs flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Done</span>
      case 'wont_do':
        return <span className="px-2 py-1 bg-gray-500/20 text-gray-400 rounded-full text-xs flex items-center gap-1"><XCircle className="w-3 h-3" /> Won't Do</span>
      default:
        return <span className="px-2 py-1 bg-gray-500/20 text-gray-400 rounded-full text-xs">{status}</span>
    }
  }

  const getCategoryBadge = (category) => {
    const colors = {
      'feature_request': 'bg-purple-500/20 text-purple-400',
      'capability_gap': 'bg-red-500/20 text-red-400',
      'integration_request': 'bg-blue-500/20 text-blue-400',
      'export_format': 'bg-green-500/20 text-green-400',
      'pattern_type': 'bg-pink-500/20 text-pink-400',
      'machine_support': 'bg-orange-500/20 text-orange-400',
      'other': 'bg-gray-500/20 text-gray-400'
    }
    return <span className={`px-2 py-1 rounded-full text-xs ${colors[category] || colors.other}`}>{category?.replace('_', ' ')}</span>
  }

  return (
    <div className="bg-gray-800/50 rounded-xl p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-white flex items-center gap-2">
          <Lightbulb className="w-6 h-6 text-yellow-400" />
          Feature Suggestions
        </h2>
        
        {/* Filter dropdown */}
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-gray-400" />
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="bg-gray-700 text-white rounded-lg px-3 py-1.5 text-sm border border-gray-600"
          >
            <option value="all">All</option>
            <option value="new">New</option>
            <option value="reviewing">Reviewing</option>
            <option value="planned">Planned</option>
            <option value="in_progress">In Progress</option>
            <option value="completed">Completed</option>
            <option value="wont_do">Won't Do</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-8 text-gray-400">Loading suggestions...</div>
      ) : suggestions.length === 0 ? (
        <div className="text-center py-8 text-gray-400">
          No suggestions yet! When users ask Zenna for features she doesn't have, they'll appear here.
        </div>
      ) : (
        <div className="space-y-4">
          {suggestions.map((suggestion) => (
            <div 
              key={suggestion.id}
              className="bg-gray-700/50 rounded-lg overflow-hidden border border-gray-600/50"
            >
              {/* Main row - always visible */}
              <div 
                className="p-4 cursor-pointer hover:bg-gray-700/70 transition-colors"
                onClick={() => loadChatContext(suggestion)}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      {getStatusBadge(suggestion.status)}
                      {getCategoryBadge(suggestion.category)}
                    </div>
                    
                    <h3 className="text-white font-medium mb-1">
                      {suggestion.suggestion_text}
                    </h3>
                    
                    <p className="text-gray-400 text-sm line-clamp-2">
                      "{suggestion.user_original_message}"
                    </p>
                    
                    <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                      <span className="flex items-center gap-1">
                        <User className="w-3 h-3" />
                        {suggestion.user_email}
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {new Date(suggestion.created_at).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    {expandedId === suggestion.id ? (
                      <ChevronUp className="w-5 h-5 text-gray-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-400" />
                    )}
                  </div>
                </div>
              </div>
              
              {/* Expanded chat context */}
              {expandedId === suggestion.id && (
                <div className="border-t border-gray-600/50 p-4 bg-gray-800/50">
                  {loadingChat ? (
                    <div className="text-center py-4 text-gray-400">Loading chat context...</div>
                  ) : chatContext ? (
                    <div className="space-y-2">
                      <h4 className="text-sm font-medium text-gray-300 mb-3 flex items-center gap-2">
                        <MessageSquare className="w-4 h-4" />
                        Chat Context (5 messages before & after)
                      </h4>
                      
                      {/* Context before - dimmed */}
                      {chatContext.before?.map((msg, i) => (
                        <div key={`before-${i}`} className="text-sm text-gray-500 pl-4 border-l-2 border-gray-600">
                          {msg}
                        </div>
                      ))}
                      
                      {/* Highlighted message - the suggestion */}
                      <div className="text-sm text-white bg-yellow-500/20 border-l-4 border-yellow-500 pl-4 py-2 rounded-r">
                        <span className="text-yellow-400 font-medium">→ </span>
                        {chatContext.highlighted || suggestion.user_original_message}
                      </div>
                      
                      {/* Context after - dimmed */}
                      {chatContext.after?.map((msg, i) => (
                        <div key={`after-${i}`} className="text-sm text-gray-500 pl-4 border-l-2 border-gray-600">
                          {msg}
                        </div>
                      ))}
                      
                      {/* Link to full chat */}
                      {suggestion.project_id && (
                        <a 
                          href={`/workspace/${suggestion.project_id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-sm text-purple-400 hover:text-purple-300 mt-2"
                        >
                          <ExternalLink className="w-4 h-4" />
                          Open full conversation
                        </a>
                      )}
                    </div>
                  ) : (
                    <div className="text-gray-400 text-sm">No chat context available</div>
                  )}
                  
                  {/* Status update buttons */}
                  <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-gray-600/50">
                    <span className="text-xs text-gray-400 mr-2">Update status:</span>
                    {['new', 'reviewing', 'planned', 'in_progress', 'completed', 'wont_do'].map(status => (
                      <button
                        key={status}
                        onClick={(e) => {
                          e.stopPropagation()
                          updateStatus(suggestion.id, status)
                        }}
                        className={`px-2 py-1 rounded text-xs transition-colors ${
                          suggestion.status === status 
                            ? 'bg-purple-500 text-white' 
                            : 'bg-gray-600 text-gray-300 hover:bg-gray-500'
                        }`}
                      >
                        {status.replace('_', ' ')}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}